import React, { useState } from 'react';
import {
  Box, Button, Chip, Collapse, Stack, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

// What a route actually is, where a truck is being put on it.
//
// The desks name the route and say why it was chosen, but never show what it means: the stages a
// load will pass, in order, and how long it is given to get between them. A counter admitting a
// truck on "Plant to Dhanbad works" has no way to check from that screen whether it is the route
// that goes through the weighbridge - and that is exactly the thing worth checking before the
// barrier goes up, because the route decides which milestones are expected and therefore which ones
// will be flagged missing later.
//
// Folded away by default. A gate desk admits trucks quickly and most of the time the route is the
// obvious one; this is for the time it is not.

const STAGE_WORDS = {
  GATE_IN: 'Gate in',
  SECURITY: 'Security',
  WEIGHBRIDGE: 'Weighbridge',
  BAY: 'Bay',
  GATE_OUT: 'Gate out',
  CLIENT: 'Customer',
};

/** The stages of a route, in order, as checkpoint records. */
export const stagesOf = (route, checkpoints) => (route?.checkpointIds || [])
  .map((id) => (checkpoints || []).find((c) => c.id === id))
  .filter(Boolean);

/** The time a route allows between one milestone and the next, in words. */
export const allowedBetweenStages = (route) => {
  const ms = Number(route?.transitMs) || 0;
  if (ms <= 0) return '';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
};

const RouteDetail = ({ route, checkpoints, dense = false }) => {
  const [open, setOpen] = useState(false);
  const stages = stagesOf(route, checkpoints);
  if (!route || !stages.length) return null;
  const allowed = allowedBetweenStages(route);

  return (
    <Box sx={{ mt: dense ? 0.5 : 1 }}>
      <Button
        size="small"
        onClick={() => setOpen((was) => !was)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none', px: 0.5 }}
        aria-expanded={open}
      >
        {open ? 'Hide route detail' : `Route detail · ${stages.length} stages`}
      </Button>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 0.5, pb: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {`${route.name}: the stages this load is expected to pass, in order.`}
            {allowed && ` Allowed between stages: ${allowed}.`}
          </Typography>
          {/* An ordered list rather than a row of chips: the order is the whole point of a route,
              and a wrapped row stops reading as a sequence the moment it wraps. */}
          <Stack component="ol" gap={0.75} sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {/* A route cannot contain the same stage twice - the server refuses it - so the stage
                id is a stable key on its own. */}
            {stages.map((stage, index) => (
              <Stack key={stage.id} component="li" direction="row" alignItems="center" gap={1}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 18 }}
                >
                  {index + 1}
                </Typography>
                <Typography variant="body2" fontWeight={600}>{stage.name}</Typography>
                <Chip size="small" variant="outlined" label={STAGE_WORDS[stage.type] || stage.type} />
                {!!stage.site && (
                  <Typography variant="caption" color="text.secondary">{stage.site}</Typography>
                )}
                {/* A stage with no geofence cannot be detected from GPS, so it will only ever be
                    recorded by somebody pressing a button. Worth knowing before the truck leaves. */}
                {!Number.isFinite(Number(stage.latitude)) && (
                  <Chip size="small" variant="outlined" color="warning" label="No geofence" />
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default RouteDetail;
